import React, { useCallback, useEffect, useState } from 'react';
import { acknowledgementsAPI } from '../api/client';
import { extractErrorMessage } from '../utils/errors';

/**
 * "You are required to acknowledge this policy" -- GitHub issue #64.
 * Mounted on DocumentViewPage.js beside ApprovalActionBar.
 *
 * Renders NOTHING when the viewer is not in the audience, which is the common
 * case: whether a policy applies to you is derived from your profile's default
 * locations against the policy's applicability tagging, and the backend answers
 * is_required=false rather than 403 for exactly this reason -- "not yours to
 * sign" is not an authorization failure.
 *
 * State always comes from the server, never held only locally -- the same rule
 * BookmarkButton and ImpersonationBanner follow, because a local flag is lost
 * on refresh and would then disagree with what is actually on file.
 *
 * DELIBERATELY NOT A ONE-CLICK TOGGLE, unlike BookmarkButton which it otherwise
 * resembles. An attestation is a legal record that a specific person read a
 * specific version, so it takes an explicit checkbox plus a button, and the
 * exact wording being agreed to is on screen (the server sends `statement`, and
 * freezes that same text onto the row as statement_snapshot). There is no
 * un-sign, so nothing here can undo one.
 */
function AcknowledgementBar({ documentId }) {
  const [status, setStatus] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await acknowledgementsAPI.forDocument(documentId);
      setStatus(res.data);
    } catch (err) {
      // Non-fatal and deliberately quiet, same reasoning ApprovalActionBar
      // gives: this must never stop the document page from rendering.
      console.error('Error loading acknowledgement status:', err);
    }
  }, [documentId]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleAcknowledge = async () => {
    setBusy(true);
    setError(null);
    try {
      await acknowledgementsAPI.acknowledge(documentId);
      await refresh();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!status || !status.is_required) return null;

  const formatDate = (value) => new Date(value).toLocaleDateString();

  if (status.acknowledged) {
    return (
      <div className="card acknowledgement-bar acknowledgement-bar--done">
        <div>
          <strong>✅ Acknowledged</strong>{' '}
          <span style={{ color: 'var(--muted)' }}>
            on {formatDate(status.acknowledged_at)}
            {status.acknowledged_version_label
              ? ` (${status.acknowledged_version_label})`
              : ''}
          </span>
          {status.due_at && (
            <div className="acknowledgement-bar__due" style={{ color: 'var(--muted)' }}>
              Next acknowledgement due {formatDate(status.due_at)}.
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="card acknowledgement-bar acknowledgement-bar--due">
      <div className="acknowledgement-bar__prompt">
        <strong>✍️ This policy requires your acknowledgement</strong>
        <p className="acknowledgement-bar__statement">{status.statement}</p>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="acknowledgement-bar__actions">
        <label className="acknowledgement-bar__confirm">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          <span>I confirm the statement above.</span>
        </label>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!confirmed || busy}
          onClick={handleAcknowledge}
        >
          {busy ? 'Recording...' : 'Acknowledge'}
        </button>
      </div>
    </div>
  );
}

export default AcknowledgementBar;
